import { computed, reactive, ref } from "vue";
import dayjs from "dayjs";
import { useStore } from "vuex";
import { formatTime, diffMinutes } from "../../utils";
import { TASK_TYPES_VALUE, USER_TYPE_SYSTEM_ADMINISTRATOR } from "core/constants";

export type TimesheetEntryLine = {
  id: string;
  expanded: boolean;
  type: "Rental" | "Force";
  startTime?: any;
  endTime?: any;
  originalLineId?: string | number;
  originalData?: any;
};

type TimesheetEntryState = {
  startDate: any;
  shift: boolean;
  lines: TimesheetEntryLine[];
};

export default function useTimesheetEntryDetail() {
  const store = useStore();
  const noon = () => dayjs().hour(12).minute(0).second(0).millisecond(0).toDate();
  const getDefaultStartDate = (): Date => {
    try {
      const settings = store.getters["setting/settings"];
      const currentUser = store.getters["auth/currentUser"] as any;
      const raw = settings?.EnableUseDefaultStartTime ? settings?.DefaultStartTime : currentUser?.defaultStartTime;
      if (raw != null && raw !== undefined) {
        const t = formatTime(raw);

        let hours24 = t.isAm ? (t.hours % 12) : (t.hours % 12) + 12;
        if (t.isAm && t.hours === 12) hours24 = 0;
        if (!t.isAm && t.hours === 12) hours24 = 12;
        return dayjs().hour(hours24).minute(t.minutes).second(0).millisecond(0).toDate();
      }
    } catch {}
    return noon();
  };
  // Get default task type based on user's taskType setting
  const getDefaultTaskType = (): "Rental" | "Force" => {
    const currentUser = store.getters["auth/currentUser"] as any;
    const userTaskType = currentUser?.taskType;
    const isSystemAdmin =
      currentUser?.userType === USER_TYPE_SYSTEM_ADMINISTRATOR;

    if (isSystemAdmin) {
      return "Rental";
    }
    
    // 0 = All (default to Rental)
    // 1 = Rental only
    // 2 = Force/JobCost only
    if (userTaskType === TASK_TYPES_VALUE.JobCost) {
      return "Force";
    }
    // Default to Rental for All (0), Rental (1), or undefined
    return "Rental";
  };

  const state = reactive<TimesheetEntryState>({
    startDate: dayjs().format("YYYY-MM-DD"),
    shift: false,
    lines: [
      { id: `new-${Date.now()}`, expanded: true, type: getDefaultTaskType(), startTime: getDefaultStartDate() },
    ],
  });

  let nextId = 2;

  const totalMinutes = computed(() => {
    let total = 0;
    for (const line of state.lines) {
      if (line.startTime && line.endTime) {
        const diff = diffMinutes(line.startTime, line.endTime);
        if (!Number.isNaN(diff)) total += diff;
      }
    }
    return Math.max(0, Math.floor(total));
  });

  const totalHoursDisplay = computed(() => String(Math.floor(totalMinutes.value / 60)).padStart(2, "0"));
  const totalMinsDisplay = computed(() => String(totalMinutes.value % 60).padStart(2, "0"));

  const validateLine = (line: TimesheetEntryLine): string | null => {
    if (!line.startTime || !line.endTime) return null;
    if (line.endTime <= line.startTime) return "End time must be after start time";
    return null;
  };

  const hasOverlap = (): boolean => {
    const ranges = state.lines
      .filter(l => l.startTime && l.endTime)
      .map(l => [l.startTime!.getTime(), l.endTime!.getTime()])
      .sort((a, b) => a[0] - b[0]);
    for (let i = 1; i < ranges.length; i++) {
      if (ranges[i][0] < ranges[i - 1][1]) return true;
    }
    return false;
  };

  // Get the maximum endTime from all existing lines
  const getMaxEndTime = (): Date | undefined => {
    let maxEnd: Date | undefined = undefined;
    for (const line of state.lines) {
      if (line.endTime) {
        if (!maxEnd || line.endTime.getTime() > maxEnd.getTime()) {
          maxEnd = line.endTime;
        }
      }
    }
    return maxEnd;
  };

  const addLine = () => {
    // New task's startTime = max endTime of existing tasks (V1 behavior)
    const maxEndTime = getMaxEndTime();
    const startTime = maxEndTime ? new Date(maxEndTime) : getDefaultStartDate();
    state.lines.push({ id: String(nextId++), expanded: true, type: getDefaultTaskType(), startTime });
  };

  // Track removed line IDs for server deletion during approval
  const removedLineIds = ref<number[]>([]);

  const removeLine = (id: string) => {
    if (state.lines.length <= 1) return;
    const idx = state.lines.findIndex(l => l.id === id);
    if (idx >= 0) {
      const line = state.lines[idx];
      // Track original line ID for server deletion
      if (line.originalLineId && typeof line.originalLineId === 'number') {
        removedLineIds.value.push(line.originalLineId);
      }
      state.lines.splice(idx, 1);
    }
  };

  const duplicateLine = (id: string, taskData?: any) => {
    const src = state.lines.find(l => l.id === id);
    if (!src) return;
    const maxEndTime = getMaxEndTime();
    const startTime = maxEndTime ? new Date(maxEndTime) : (src.startTime ? new Date(src.startTime) : noon());
    
    // Build duplicated data if taskData is provided
    let duplicatedData: any = undefined;
    if (taskData) {
      duplicatedData = {
        // Common fields
        SiteProjectID: taskData.siteProjectId || undefined,
        OverHead: taskData.overHead ?? false,
        FieldService: taskData.fieldService ?? false,
        NPCode: taskData.overHead ? (taskData.npCode || undefined) : undefined,
        
        // Rental-specific fields
        AssetProjectID: taskData.assetProjectId || undefined,
        WorkOrderProjectID: taskData.workOrderProjectId || undefined,
        
        // Force-specific fields  
        JobProjectID: taskData.jobProjectId || undefined,
        ActivityId: taskData.activityId || undefined,
        
        // Field Service / Additional Info
        TimesheetAdditionals: taskData.timesheetAdditional ? [{
          Manufacturer: taskData.timesheetAdditional.manufacturer || '',
          Model: taskData.timesheetAdditional.model || '',
          Smureading: taskData.timesheetAdditional.smureading || 0,
          SerialNumber: taskData.timesheetAdditional.serialNumber || '',
          CustomerName: taskData.timesheetAdditional.customerName || '',
          CustomerEmail: taskData.timesheetAdditional.customerEmail || '',
          Locations: taskData.timesheetAdditional.locations || '',
          TravelKms: taskData.timesheetAdditional.travelKms || 0,
          Rego: taskData.timesheetAdditional.rego || '',
          // Note: signatures are NOT copied (user needs to re-sign)
          WorkerSignature: '',
          CustomerSignature: '',
          AdditionalInfo: taskData.timesheetAdditional.additionalInfo || '',
          CustomerAcceptanceName: taskData.timesheetAdditional.customerAcceptanceName || '',
        }] : undefined,
        
        // Mark as copy so components know this is duplicated data
        isCopy: true,
      };
    }
    
    state.lines.push({
      id: String(nextId++),
      expanded: true,
      type: src.type,
      startTime,
      endTime: undefined, // Reset endTime for duplicate
      originalData: duplicatedData, // Pass copied task data
    });
  };

  const toggleExpanded = (id: string) => {
    const line = state.lines.find(l => l.id === id);
    if (line) line.expanded = !line.expanded;
  };

  const setType = (id: string, type: TimesheetEntryLine["type"]) => {
    const line = state.lines.find(l => l.id === id);
    if (line) line.type = type;
  };

  const setShift = (isNight: boolean) => {
    state.shift = isNight;
  };

  const setStartDate = (val: string) => {
    state.startDate = val;
  };

  const setLineDuration = (id: string, hours: number, minutes: number) => {
    const line = state.lines.find(l => l.id === id);
    if (!line) return;
    if (!line.startTime) {
      line.startTime = getDefaultStartDate();
    }
    const total = Math.max(0, Math.floor(hours) * 60 + Math.max(0, Math.floor(minutes)));
    if (total === 0) {
      line.endTime = undefined;
      return;
    }
    line.endTime = dayjs(line.startTime).add(total, 'minute').toDate();
  };

  const resetForm = () => {
    const newStartTime = getDefaultStartDate();
    state.startDate = dayjs().format("YYYY-MM-DD");
    state.shift = false;
    const uniqueId = `new-${Date.now()}`;
    state.lines = [
      { id: uniqueId, expanded: true, type: getDefaultTaskType(), startTime: newStartTime },
    ];
    removedLineIds.value = []; // Clear removed lines tracking
    nextId = 2;
  };

  const validateWorkingTime = () => {

    const hasInvalidLine = state.lines.some(line => !line.startTime || !line.endTime);
    return {
      isValid: !hasInvalidLine,
      message: hasInvalidLine ? "Please choose working time" : null
    };
  };

  // Load existing timesheet data for edit mode
  const loadTimesheetData = (timesheetData: any) => {
    if (!timesheetData) return;

    // Set header data
    state.startDate = timesheetData.startDate 
      ? dayjs(timesheetData.startDate).format("YYYY-MM-DD") 
      : dayjs().format("YYYY-MM-DD");
    state.shift = timesheetData.shift ?? false;
    
    // Clear existing lines
    state.lines = [];

    // Load lines from timesheet
    const lines = timesheetData.timesheetLines || timesheetData.TimesheetLines || [];
    if (lines.length === 0) {
      // Add default line if no lines
      state.lines.push({ id: `new-${Date.now()}`, expanded: true, type: getDefaultTaskType(), startTime: getDefaultStartDate() });
      nextId = 2;
      return;
    }

    lines.forEach((line: any, index: number) => {
      const id = String(line.TimesheetLineId || line.timesheetLineId || index + 1);
      
      // Determine type based on timesheetLineType (0 = Rental, 1 = Force)
      const lineType = line.TimesheetLineType ?? line.timesheetLineType ?? 0;
      const type: "Rental" | "Force" = lineType === 0 ? "Rental" : "Force";


      // Parse start/end times
      let startTime: Date | undefined;
      let endTime: Date | undefined;

      if (line.StartTime !== undefined || line.startTime !== undefined) {
        const rawStartTime = line.StartTime ?? line.startTime;
        if (typeof rawStartTime === 'number') {
          startTime = dayjs().hour(Math.floor(rawStartTime / 60)).minute(rawStartTime % 60).second(0).millisecond(0).toDate();
        } else if (typeof rawStartTime === 'string' && rawStartTime.includes(':')) {
          // Handle string TimeSpan format like "08:00:00"
          const parts = rawStartTime.split(':');
          const h = parseInt(parts[0], 10);
          const m = parseInt(parts[1], 10);
          if (!isNaN(h) && !isNaN(m)) {
            startTime = dayjs().hour(h).minute(m).second(0).millisecond(0).toDate();
          }
        }
      }

      if (line.EndTime !== undefined || line.endTime !== undefined) {
        const rawEndTime = line.EndTime ?? line.endTime;
        if (typeof rawEndTime === 'number') {
          endTime = dayjs().hour(Math.floor(rawEndTime / 60)).minute(rawEndTime % 60).second(0).millisecond(0).toDate();
        } else if (typeof rawEndTime === 'string' && rawEndTime.includes(':')) {
           // Handle string TimeSpan format like "12:00:00"
           const parts = rawEndTime.split(':');
           const h = parseInt(parts[0], 10);
           const m = parseInt(parts[1], 10);
           if (!isNaN(h) && !isNaN(m)) {
             endTime = dayjs().hour(h).minute(m).second(0).millisecond(0).toDate();
           }
        }
      }

      const newLine = {
        id,
        expanded: true,
        type,
        startTime,
        endTime,
        // Store original line data for updates and for passing to child components
        originalLineId: line.TimesheetLineId || line.timesheetLineId,
        originalData: line, // Store full API line data to pass down to ForceTaskDetail/RentalTaskDetail
      };
      state.lines.push(newLine);
    });

    nextId = Math.max(...state.lines.map(l => parseInt(l.id) || 0)) + 1;
  };

  return {
    state,
    removedLineIds,
    totalMinutes,
    totalHoursDisplay,
    totalMinsDisplay,
    validateLine,
    hasOverlap,
    addLine,
    removeLine,
    duplicateLine,
    toggleExpanded,
    setType,
    setShift,
    setStartDate,
    setLineDuration,
    resetForm,
    validateWorkingTime,
    loadTimesheetData,
  };
}


